import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "design-system/molecules/Card"
import { NewBadge } from "design-system/molecules/NewBadge"
import { cn } from "design-system/utils"
import { isFilled } from "lopermut"
import { AlertCircle, AlertTriangle, List, Pencil, Plus } from "lucide-react"
import { CapabilityDialog } from "modules/capabilities/components/CapabilityDialog"
import { CapabilityOverrideDialog } from "modules/capabilities/components/CapabilityDialog/CapabilityOverrideDialog"
import { useCapabilitiesDictionary } from "modules/capabilities/hooks/useCapabilitiesDictionary"
import { useCapabilityActions } from "modules/capabilities/hooks/useCapabilityActions"
import { useKindBadge } from "modules/capabilities/hooks/useKindBadge"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesMarketplaceResponseDataSchema,
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityKind,
} from "schema"
import z from "zod"
import { CapabilityCardActionsMenu } from "./CapabilityCardActionsMenu"

type CampaignContext = {
  campaignId: string
  isActiveForCampaign: boolean
  hasOverride: boolean
  isTogglePending: boolean
  onToggle: (enabled: boolean) => Promise<void>
}

type CapabilityCardProps = {
  capability:
    | z.infer<typeof CapabilitiesResponseDataSchema>[number]
    | z.infer<typeof CapabilitiesMarketplaceResponseDataSchema>[number]
  campaignContext?: CampaignContext
}

function getCampaignFlowLabel(t: (key: string) => string, campaignFlow: string | null | undefined) {
  if (campaignFlow === "Inbound") return t("capabilities.activation.flowInbound")
  return t("capabilities.activation.flowOutbound")
}

function getAssignationLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  capability: {
    campaignAssignation?: CapabilityCampaignAssignation
    campaignFlow?: string | null
    campaignIds: string[]
  },
) {
  if (capability.campaignAssignation === "AllCampaigns")
    return t("capabilities.list.activeToAllCampaigns")
  if (capability.campaignAssignation === "CampaignFlow")
    return getCampaignFlowLabel(t, capability.campaignFlow)
  return t("capabilities.list.activeToCampaigns", { count: capability.campaignIds.length })
}

export function CapabilityCard({ capability, campaignContext }: CapabilityCardProps) {
  const { t } = useTranslation()
  const capabilitiesDictionary = useCapabilitiesDictionary()
  const { canInstall, installDisabledReason } = useCapabilityActions(capability)
  const getBadge = useKindBadge()

  const [isInstallDialogOpen, setIsInstallDialogOpen] = useState(false)
  const [isConfigureDialogOpen, setIsConfigureDialogOpen] = useState(false)
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false)

  const { name, description, detailedDescription } =
    capabilitiesDictionary[capability.identifier as keyof typeof capabilitiesDictionary]

  const kind = capability.kind
  const isInstalled = "id" in capability
  const isCampaignMode = Boolean(campaignContext)
  const isActiveForCampaign = campaignContext?.isActiveForCampaign ?? true

  const isActiveToSomeCampaign =
    ("campaignAssignation" in capability && capability.campaignAssignation !== "HandPicked") ||
    ("campaignIds" in capability && capability.campaignIds.length > 0)

  const installButton = useMemo(() => {
    if (isInstalled || isCampaignMode) return null

    const isDisabled = !canInstall || isInstallDialogOpen

    const button = (
      <Button
        disabled={isDisabled}
        onClick={() => setIsInstallDialogOpen(true)}
        size="default"
        title={t("capabilities.list.install.cta")}
      >
        <Plus className="size-4" />
        <span>{t("capabilities.list.install.cta")}</span>
      </Button>
    )

    if (!canInstall) {
      return (
        <Tooltip>
          <TooltipTrigger className="rounded-lg">{button}</TooltipTrigger>
          <TooltipContent
            variant="card"
            side="bottom"
            className="flex flex-col items-start gap-0.5 p-4 shadow-md"
          >
            <div className="flex flex-row items-center gap-1">
              <AlertCircle className="size-4 shrink-0 text-muted-foreground" strokeWidth={2} />
              <p className="text-sm font-semibold text-foreground leading-normal">
                {t("capabilities.list.install.disabled.title")}
              </p>
            </div>
            <div className="text-sm text-muted-foreground leading-normal whitespace-pre-line text-left">
              {installDisabledReason}
            </div>
          </TooltipContent>
        </Tooltip>
      )
    }

    return button
  }, [isInstalled, isCampaignMode, canInstall, isInstallDialogOpen, installDisabledReason, t])

  const cardTitle = useMemo(() => {
    const isCustom =
      kind === CapabilityKind.Webhook ||
      kind === CapabilityKind.Instruction ||
      kind === CapabilityKind.MCP
    if (isCustom && "name" in capability && isFilled(capability.name)) {
      return capability.name
    }
    return name
  }, [kind, capability, name])

  return (
    <>
      <Card
        key={capability.identifier}
        className={cn(
          "overflow-hidden h-full flex flex-col justify-between",
          isCampaignMode && !isActiveForCampaign && "opacity-75",
        )}
      >
        <CardHeader className="flex flex-col items-start text-start gap-1.5 pb-3.5 relative">
          {!isCampaignMode && !isActiveToSomeCampaign && isInstalled && (
            <Badge variant="negative" className="shrink-0">
              <AlertTriangle className="size-3" />
              <span>{t("capabilities.list.notActiveToAnyCampaign")}</span>
            </Badge>
          )}
          <div className="flex flex-row items-start justify-between gap-2 w-full">
            <CardTitle className="text-lg">{cardTitle}</CardTitle>
            <div className="flex flex-row items-center gap-1">
              {campaignContext?.hasOverride && (
                <Badge variant="outline">{t("campaigns.capabilities.override.badge")}</Badge>
              )}
              {capability.isNew && <NewBadge label={t("shared.new")} />}
              {getBadge(capability.kind)}
            </div>
          </div>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardFooter className="flex flex-col sm:flex-row justify-between sm:items-end items-start gap-3">
          <div className="flex flex-row items-center flex-wrap gap-2">
            {isCampaignMode && isInstalled ? (
              <>
                <Badge variant="gradient">
                  <span>{t("capabilities.list.timesUsed", { count: capability.timesUsed })}</span>
                </Badge>
                {capability.campaignAssignation !== "HandPicked" ? (
                  <Badge variant="success">
                    {capability.campaignAssignation === "CampaignFlow"
                      ? getCampaignFlowLabel(t, capability.campaignFlow)
                      : t("campaigns.capabilities.activeForAll")}
                  </Badge>
                ) : (
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`capability-toggle-${capability.id}`}
                      checked={isActiveForCampaign}
                      onCheckedChange={campaignContext!.onToggle}
                      disabled={campaignContext!.isTogglePending}
                    />
                    <Label
                      className="text-sm text-muted-foreground cursor-pointer select-none"
                      htmlFor={`capability-toggle-${capability.id}`}
                    >
                      {t("campaigns.capabilities.toggle")}
                    </Label>
                  </div>
                )}
              </>
            ) : (
              !isCampaignMode &&
              isInstalled && (
                <>
                  <Badge variant="gradient">
                    <span>{t("capabilities.list.timesUsed", { count: capability.timesUsed })}</span>
                  </Badge>
                  <Badge variant="success">
                    <span>{getAssignationLabel(t, capability)}</span>
                  </Badge>
                </>
              )
            )}
          </div>
          <div className="flex flex-row items-center justify-end flex-wrap gap-2">
            {installButton}
            {!isInstalled && detailedDescription && (
              <Tooltip>
                <TooltipTrigger className="rounded-lg">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="icon" title={t("capabilities.list.details")}>
                        <List className="size-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{name}</DialogTitle>
                        <DialogDescription>{detailedDescription}</DialogDescription>
                      </DialogHeader>
                    </DialogContent>
                  </Dialog>
                </TooltipTrigger>
                <TooltipContent>{t("capabilities.list.details")}</TooltipContent>
              </Tooltip>
            )}
            {isInstalled && !isCampaignMode && (
              <Button
                variant="default"
                onClick={() => setIsConfigureDialogOpen(true)}
                size="default"
                title={t("capabilities.list.edit")}
                className="px-3"
              >
                <Pencil className="size-4" />
              </Button>
            )}
            {isCampaignMode && isInstalled && capability.supportsConfigOverride && (
              <Button
                variant="default"
                size="default"
                onClick={() => setIsOverrideDialogOpen(true)}
              >
                <Pencil className="size-4" />
                <span>{t("campaigns.capabilities.override.configure")}</span>
              </Button>
            )}
            {isInstalled && (
              <CapabilityCardActionsMenu
                capability={capability}
                campaignId={campaignContext?.campaignId}
              />
            )}
          </div>
        </CardFooter>
      </Card>
      {isCampaignMode &&
        isInstalled &&
        capability.supportsConfigOverride &&
        isOverrideDialogOpen && (
          <CapabilityOverrideDialog
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            campaignId={campaignContext!.campaignId}
            open={isOverrideDialogOpen}
            onOpenChange={setIsOverrideDialogOpen}
            capabilityName={cardTitle}
          />
        )}
      {!isCampaignMode && !isInstalled && isInstallDialogOpen && (
        <CapabilityDialog
          capability={
            capability as z.infer<typeof CapabilitiesMarketplaceResponseDataSchema>[number]
          }
          mode="install"
          onOpenChange={setIsInstallDialogOpen}
        />
      )}
      {!isCampaignMode && isInstalled && isConfigureDialogOpen && (
        <CapabilityDialog
          capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
          mode="configure"
          onOpenChange={setIsConfigureDialogOpen}
        />
      )}
    </>
  )
}
